import { SpavaObj } from "./SpavaObj";
import { Person } from "./UserAdmin";
import { ParkingLot, Controller } from "./ParkingLot";
import { F, time } from "@/ladmin";

class Vehicle extends SpavaObj {
  static RESERVATION = "reservation";
  static REGISTER = "register";
  static PASS_GROUP = "passGroup";
  static UNKNOWN = "unknown";
  plate;
  plateType = null;
  owner = null;
  vehicleType = Vehicle.UNKNOWN;
  freezeStatus = "";
  freezeTime = "";
  constructor(o) {
    if (o instanceof Vehicle) return o;
    super(o);
    if (typeof o === "string") {
      this.plate = o;
    }
    o = o instanceof Object ? o : {};
    this.plate = o.plate || "";
    if (o.owner instanceof Person) {
      this.owner = o.owner;
    } else if (o.owner instanceof Object) {
      this.owner = new Person(o.owner);
    }
    this.vehicleType = o.vehicleType || Vehicle.UNKNOWN;
    this.freezeStatus = o.freezeStatus || "";
    this.freezeTime = o.freezeTime;
  }

  get name() {
    return this.owner && this.owner.name;
  }

  set name(val) {
    this.owner
      ? (this.owner.name = val)
      : (this.owner = new Person({ name: val }));
  }

  get phone() {
    return this.owner && this.owner.phone;
  }

  set phone(val) {
    this.owner
      ? (this.owner.phone = val)
      : (this.owner = new Person({ phone: val }));
  }

  get orgName() {
    return this.owner && this.owner.orgName;
  }

  get typeName() {
    return this.owner && this.owner.typeName;
  }

  get loginId() {
    return this.owner && this.owner.loginId;
  }

  get isReservation() {
    return this.vehicleType === Vehicle.RESERVATION;
  }

  get isRegister() {
    return this.vehicleType === Vehicle.REGISTER;
  }

  get isPassGroup() {
    return this.vehicleType === Vehicle.PASS_GROUP;
  }
}

class Gateway extends ParkingLot {
  controllers;

  constructor(o) {
    super(o);
    o = o || {};
    this.controllers =
      o.controllers instanceof Array
        ? o.controllers.map(c =>
            c instanceof Controller ? c : new Controller(c)
          )
        : [];
  }
}

class TimeSlice {
  index;
  id;
  startWeekday;
  startTime;
  endWeekday;
  endTime;
  gateways;

  constructor(o) {
    if (typeof o === "number") {
      this.index = o;
    }
    o = o || {};
    this.index = typeof o.index === "number" ? o.index : this.index;
    this.id = o.id || "";
    this.startWeekday = o.startWeekday || "";
    this.startTime = o.startTime || "";
    this.endWeekday = o.endWeekday || "";
    this.endTime = o.endTime || "";
    this.gateways =
      o.gateways instanceof Array ? o.gateways.map(g => new Gateway(g)) : [];
  }
}

class PassRule extends SpavaObj {
  groupId;
  index;
  details;
  ruleType;
  startDatetime;
  endDatetime;
  period;
  slices;
  gateways;
  ledPrompt;
  audioPrompt;
  ledPromptIn;
  audioPromptIn;
  ledPromptOut;
  audioPromptOut;
  description;
  status;
  ownerType;

  constructor(o) {
    super(o);
    if (typeof o === "number") {
      this.index = o;
    }
    o = o || {};
    this.index = typeof o.index === "number" ? o.index : this.index;
    this.details = o.details || "";
    this.ruleType = o.ruleType || "sectional";
    this.startDatetime = o.startDatetime || "";
    this.endDatetime = o.endDatetime || "";
    this.period = o.period || "day";
    this.slices =
      o.slices instanceof Array ? o.slices.map(s => new TimeSlice(s)) : [];
    this.gateways =
      o.gateways instanceof Array ? o.gateways.map(g => new Gateway(g)) : [];
    this.ledPrompt = o.ledPrompt || "";
    this.audioPrompt = o.audioPrompt || "";
    this.ledPromptIn = o.ledPromptIn || "";
    this.audioPromptIn = o.audioPromptIn || "";
    this.ledPromptOut = o.ledPromptOut || "";
    this.audioPromptOut = o.audioPromptOut || "";
    this.description = o.description || "";
    this.groupId = o.groupId;
    this.status = o.status;
    this.ownerType = o.ownerType;
  }

  get sliceAmount() {
    return this.slices.length;
  }

  toAPI(type) {
    function gateways2controllers(gateways) {
      let controllers = [];
      if (gateways instanceof Array) {
        gateways.forEach(gateway => {
          gateway.controllers.forEach(c => {
            controllers.push(c.id);
          });
        });
      }
      return [...new Set(controllers)];
    }

    let rule = this;
    let ruleData = {
      id: rule.id || "",
      description: rule.description || "",
      ownerId: rule.groupId || "",
      ownerType: type || "group",
      segmentType: rule.ruleType === "periodic" ? rule.period : "",
      sdtime: time.toTimeStamp(rule.startDatetime).toString(),
      edtime: time.toTimeStamp(rule.endDatetime).toString(),
      vcbMsgIn: rule.audioPromptIn,
      osdMsgIn: rule.ledPromptIn,
      vcbMsgOut: rule.audioPromptOut,
      osdMsgOut: rule.ledPromptOut,
      config: ""
    };
    if (rule.slices && ruleData.segmentType) {
      let config = {
        period: rule.period,
        sctn_amnt: rule.slices.length,
        sctns: []
      };
      let slices = (rule.slices || []).slice(0);
      let t = {
        endTime: "etime",
        endWeekday: "ewkday",
        id: "sctn_id",
        startTime: "stime",
        startWeekday: "swkday"
      };
      F.transformIO(slices, t);
      slices.forEach((slice, index) => {
        slice.controllers = gateways2controllers(slice.gateways);
        slice.sctn_id = index + "";
        slice.stime += ":00";
        slice.etime += ":00";
        delete slice.index;
        delete slice.gateways;
      });
      config.sctns = slices;
      ruleData.config = JSON.stringify(config);
    } else if (!ruleData.segmentType) {
      let config = {
        controllers: gateways2controllers(rule.gateways)
      };
      ruleData.config = JSON.stringify(config);
    }
    return ruleData;
  }

  static fromAPI(o, controllerList) {
    if (!(controllerList instanceof Array)) {
      throw Error("controllerList need be Array");
    }
    let rule = Object.assign({}, o);
    rule.groupId = rule.ownerId;
    if (o.rhdata !== undefined) {
      rule.status = JSON.parse(o.rhdata).r_h_status;
    }
    let ruleTransDict = {
      sdtime: "startDatetime",
      edtime: "endDatetime",
      vcbMsgAllowed: { to: "audioPrompt", type: String, default: "" },
      osdMsgAllowed: { to: "ledPrompt", type: String, default: "" },
      vcbMsgIn: { to: "audioPromptIn", type: String, default: "" },
      osdMsgIn: { to: "ledPromptIn", type: String, default: "" },
      vcbMsgOut: { to: "audioPromptOut", type: String, default: "" },
      osdMsgOut: { to: "ledPromptOut", type: String, default: "" },
      segmentType: "ruleType"
    };

    F.transformIO(rule, ruleTransDict);
    rule.period = rule.ruleType;
    rule.ruleType = rule.ruleType ? "periodic" : "sectional";
    if (rule.config && typeof rule.config === 'string') {
      try {
        rule.config = JSON.parse(rule.config);
      } catch (e) {
      }
    } else if (!(rule.config instanceof Object)) {
      rule.config = {};
    }

    rule.sliceAmout = rule.config.sctn_amnt || 0;
    rule.slices = rule.config.sctns || [];

    let t = {
      etime: "endTime",
      ewkday: "endWeekday",
      sctn_id: "id",
      stime: "startTime",
      swkday: "startWeekday"
    };
    F.transformIO(rule.slices, t);

    rule.startDatetime = time.stringify(rule.startDatetime, time.DT);
    rule.endDatetime = time.stringify(rule.endDatetime, time.DT);
    rule.slices.forEach(slice => {
      slice.startTime = slice.startTime.substr(0, 5);
      slice.endTime = slice.endTime.substr(0, 5);
      let gateways = [];
      slice.controllers.forEach(cId => {
        let controller = controllerList.find(v => v.id === cId);
        // console.log('find controllers', controller)
        if (!controller) return;

        let p = gateways.find(g => g.id === controller.parkingLotId);
        if (p) {
          p.controllers.push(controller);
        } else {
          gateways.push({
            name: controller.parkingLotName,
            id: controller.parkingLotId,
            controllers: [controller]
          });
        }
      });
      slice.gateways = gateways;
    });
    if (rule.ruleType === "sectional") {
      let gateways = [];
      if (!(rule.config.controllers instanceof Array)) {
        rule.config.controllers = [];
      }
      rule.config.controllers.forEach(cId => {
        let controller = controllerList.find(v => v.id === cId);
        // console.log('find controllers', controller)
        if (!controller) return;

        let p = gateways.find(g => g.id === controller.parkingLotId);
        if (p) {
          p.controllers.push(controller);
        } else {
          gateways.push({
            name: controller.parkingLotName,
            id: controller.parkingLotId,
            controllers: [controller]
          });
        }
      });
      // console.log('get rule getways', gateways)
      rule.gateways = gateways;
    }
    return new PassRule(rule);
  }
}

class SpecificRule extends PassRule {
  vehicle;
  isStandalone = false;

  constructor(o) {
    super(o);
    o = o || {};
    this.vehicle = SpavaObj.getObjKey(o.vehicle, Vehicle);
    if (this.vehicle !== null) {
      this.vehicle.objectId = this.vehicle.plate;
    }
    this.isStandalone = o.isStandalone;
  }

  static fromAPI(o, controllerList) {
    let p = super.fromAPI(o, controllerList);
    p.vehicle = { plate: o.ownerId };
    p.isStandalone = o.isStandalone;
    return new SpecificRule({ ...p });
  }

  toAPI() {
    let r = super.toAPI();
    r.ownerType = "object";
    r.ownerId = this.plate;
    return r;
  }

  get plate() {
    return this.vehicle && this.vehicle.plate;
  }

  set plate(val) {
    this.vehicle
      ? (this.vehicle.plate = val)
      : (this.vehicle = new Vehicle({ plate: val }));
  }
}

class PassGroup extends SpavaObj {
  name;
  description;
  type;
  ledPromptIn;
  audioPromptOut;
  ledPromptOut;
  audioPromptIn;
  expirationTime;
  priority;

  rules;
  specifics;
  vehicles;

  constructor(o) {
    super(o);
    o = o instanceof Object ? o : {};
    this.type = o.type || null;
    this.name = o.name || "";
    this.description = o.description || "";
    this.ledPromptIn = o.ledPromptIn || "";
    this.audioPromptOut = o.audioPromptOut || "";
    this.ledPromptOut = o.ledPromptOut || "";
    this.audioPromptIn = o.audioPromptIn || "";
    this.expirationTime = o.expirationTime || null;
    this.priority = o.priority || null;
    this.rules = SpavaObj.getObjKey(o.rules, PassRule, { array: true });
    this.specifics = SpavaObj.getObjKey(o.specifics, PassRule, { array: true });
    this.vehicles = SpavaObj.getObjKey(o.vehicles, PassRule, { array: true });
  }

  static fromAPI(o) {
    let transDict = {
      vcbMsgAllowed: { to: "audioPrompt", type: String, default: "" },
      osdMsgAllowed: { to: "ledPrompt", type: String, default: "" },
      vcbMsgIn: { to: "audioPromptIn", type: String, default: "" },
      osdMsgIn: { to: "ledPromptIn", type: String, default: "" },
      vcbMsgOut: { to: "audioPromptOut", type: String, default: "" },
      osdMsgOut: { to: "ledPromptOut", type: String, default: "" }
    };
    F.transformIO(o, transDict);
    return new PassGroup(o);
  }
}

class PassLog extends SpavaObj {
  vehicle;
  passDatetime;
  details;
  controller;
  parkingLot;
  status;
  name;
  org;
  passType;
  remark;
  phone;
  loginId;
  userType;
  ecardId;
  applicantName;
  applicantOrgName;
  applicantOrgId;
  passRuleType;

  constructor(o) {
    super(o);
    o = o instanceof Object ? o : {};
    this.vehicle =
      o.vehicle instanceof Vehicle
        ? o.vehicle
        : o.vehicle
        ? new Vehicle(o.vehicle)
        : null;
    this.passDatetime = o.passDatetime;
    this.details = o.details;
    this.controller = o.controller ? new Controller(o.controller) : null;
    this.parkingLot = o.parkingLot ? new ParkingLot(o.parkingLot) : null;
    this.status = o.status || "0";
  }

  get plate() {
    return this.vehicle && this.vehicle.plate;
  }
  get parkingLotName() {
    return this.parkingLot && this.parkingLot.name;
  }
  get controllerName() {
    return this.controller && this.controller.name;
  }

  static fromAPI(info, controllerList = []) {
    let o = info.passlog;
    o.passDatetime = o.passTimeController;
    let l = new PassLog(o);
    l.vehicle = new Vehicle({ plate: o.objectId });
    l.details = o.remark || "";
    l.controller = controllerList.find(c => c.id === o.controllerId);
    l.parkingLot = l.controller && l.controller.parkingLot;
    l.remark = o.remark || "";
    l.name = o.name || "N/A";
    l.phone = o.phone || "N/A";
    l.passType = o.passType || "N/A";
    l.loginId = o.loginId || "N/A";
    l.org = o.org || "N/A";
    l.userType = o.userType || "N/A";
    l.ecardId = o.ecardId || "N/A";
    l.applicantName = o.applicantName || "";
    l.applicantOrgName = o.applicantOrgName || "";
    l.applicantOrgId = o.applicantOrgId || "";
    l.passRuleType = o.passRuleType || "";
    return l;
  }
}

export {
  PassGroup,
  Vehicle,
  PassRule,
  TimeSlice,
  Gateway,
  SpecificRule,
  PassLog
};
